import { Alert, Button, Logo } from '@rentflowdev/ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { oauthApi } from '../api/client'
import { redirectWithCode, redirectWithError } from '../lib/redirect'
import { useOAuth } from '../providers/OAuthProvider'

export function ConsentPage() {
	const { t } = useTranslation()
	const { params, client, accountId } = useOAuth()

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	if (!params || !client || !accountId) {
		return (
			<div className="w-full max-w-sm">
				<div className="rounded-2xl bg-white shadow-[0_1px_3px_0_rgb(0_0_0_/_0.08)] border border-neutral-100 p-8">
					<Alert variant="error">{t('authorize.error')}</Alert>
				</div>
			</div>
		)
	}

	const scopes = params.scope.split(' ').filter(Boolean)
	const redirectDomain = (() => {
		try { return new URL(params.redirectUri).hostname } catch { return params.redirectUri }
	})()

	const handleAuthorize = async () => {
		setError(null)
		setLoading(true)
		try {
			const result = await oauthApi.postAuthorize(params)
			redirectWithCode(params, result)
		} catch (err) {
			setError(err instanceof Error ? err.message : t('error.generic'))
			setLoading(false)
		}
	}

	const handleDeny = () => {
		redirectWithError(params, 'access_denied')
	}

	return (
		<div className="w-full max-w-sm">
			<div className="flex flex-col items-center gap-8">
				<Logo size="md" />

				<div className="w-full rounded-2xl bg-white shadow-[0_1px_3px_0_rgb(0_0_0_/_0.08)] border border-neutral-100 p-8">
					<div className="mb-6">
						<h1 className="text-xl font-semibold text-neutral-900">{t('consent.title')}</h1>
						<p className="text-sm text-neutral-500 mt-1">
							{t('consent.requesting', { name: client.name })}
						</p>
					</div>

					<div className="flex flex-col gap-4">
						{scopes.length > 0 && (
							<div className="rounded-(--radius-md) border border-neutral-200 divide-y divide-neutral-100">
								{scopes.map((scope) => (
									<div key={scope} className="flex items-center gap-3 px-4 py-3">
										<svg className="h-4 w-4 text-primary-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
										<span className="text-sm text-neutral-700">
											{t(`consent.scopes.${scope}`, { defaultValue: scope })}
										</span>
									</div>
								))}
							</div>
						)}

						<p className="text-xs text-neutral-400">
							{t('consent.redirectTo')}{' '}
							<span className="font-medium text-neutral-600">{redirectDomain}</span>
						</p>

						{error && <Alert variant="error">{error}</Alert>}

						<div className="flex flex-col gap-2">
							<Button size="full" onClick={() => void handleAuthorize()} loading={loading}>
								{t('consent.authorize')}
							</Button>
							<Button size="full" variant="secondary" onClick={handleDeny} disabled={loading}>
								{t('consent.deny')}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
